import React, { useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';

interface ProductFormProps {
  product?: {
    _id: string;
    name: string;
    price: number;
    image_url: string;
    description: string[];
    execute: { type: string; text: string; }[];
    sort_id: number;
  };
  onSuccess: () => void;
  onCancel: () => void;
}

export function ProductForm({ product, onSuccess, onCancel }: ProductFormProps) {
  const [name, setName] = useState(product?.name || '');
  const [price, setPrice] = useState(product?.price || 0);
  const [imageUrl, setImageUrl] = useState(product?.image_url || '');
  const [description, setDescription] = useState(product?.description?.join('\n') || '');
  const [execute, setExecute] = useState(
    product?.execute?.map(e => e.text).join('\n') || ''
  );
  const [sortId, setSortId] = useState(product?.sort_id || 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(product?.image_url || null);

  const handleImageUrlChange = (url: string) => {
    setImageUrl(url);
    setImagePreview(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const formData = {
        name,
        price: Number(price),
        image_url: imageUrl,
        description: description.split('\n').filter(Boolean),
        execute: execute.split('\n').filter(Boolean).map(text => ({
          type: 'commands',
          text
        })),
        sort_id: Number(sortId)
      };

      const response = await fetch(
        product
          ? API_ENDPOINTS.admin.product(product._id)
          : API_ENDPOINTS.admin.products,
        {
          method: product ? 'PUT' : 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save product');
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left Column */}
        <div className="space-y-10">
          <div>
            <label htmlFor="name" className="block text-lg font-medium text-white mb-3">
              Product Name
            </label>
            <input
              type="text"
              id="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-lg border-2 border-gray-700 bg-gray-800 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-lg py-4 px-5"
              placeholder="e.g., 40 Premium Points"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label htmlFor="price" className="block text-lg font-medium text-white mb-3">
                Price (IDR)
              </label>
              <input
                type="number"
                id="price"
                required
                value={price}
                onChange={(e) => setPrice(Number(e.target.value))}
                className="mt-1 block w-full rounded-lg border-2 border-gray-700 bg-gray-800 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-lg py-4 px-5"
                placeholder="e.g., 40000"
                min="0"
                step="1000"
              />
            </div>

            <div>
              <label htmlFor="sortId" className="block text-lg font-medium text-white mb-3">
                Sort Order
              </label>
              <input
                type="number"
                id="sortId"
                required
                value={sortId}
                onChange={(e) => setSortId(Number(e.target.value))}
                className="mt-1 block w-full rounded-lg border-2 border-gray-700 bg-gray-800 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-lg py-4 px-5"
                placeholder="e.g., 1"
                min="0"
              />
              <p className="mt-2 text-sm text-gray-400">Lower numbers appear first</p>
            </div>
          </div>

          <div>
            <label htmlFor="imageUrl" className="block text-lg font-medium text-white mb-3">
              Image URL
            </label>
            <div className="flex gap-6">
              <div className="flex-1">
                <input
                  type="text"
                  id="imageUrl"
                  required
                  value={imageUrl}
                  onChange={(e) => handleImageUrlChange(e.target.value)}
                  className="mt-1 block w-full rounded-lg border-2 border-gray-700 bg-gray-800 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-lg py-4 px-5"
                  placeholder="e.g., /images/prp_040.webp"
                />
              </div>
              {imagePreview && (
                <div className="relative w-40 h-40 rounded-lg overflow-hidden bg-gray-700/50 border-2 border-gray-700 flex-shrink-0">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={() => setImagePreview(null)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-10">
          <div>
            <label htmlFor="description" className="block text-lg font-medium text-white mb-3">
              Description
            </label>
            <div className="relative">
              <textarea
                id="description"
                required
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={8}
                className="mt-1 block w-full rounded-lg border-2 border-gray-700 bg-gray-800 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-lg py-4 px-5"
                placeholder="Enter each description line separately&#10;e.g., Donasi sebesar Rp40.000 untuk mendapatkan 40 Premium Points&#10;Tunggu beberapa menit setelah pembayaran"
              />
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-gray-800 to-transparent pointer-events-none" />
            </div>
            <div className="mt-3 flex items-center space-x-2">
              <div className="w-5 h-5 rounded-full bg-primary-500/20 border border-primary-500/40 flex items-center justify-center">
                <span className="text-primary-500 text-sm">i</span>
              </div>
              <p className="text-base text-gray-400">Each line will be displayed as a separate bullet point</p>
            </div>
          </div>

          <div>
            <label htmlFor="execute" className="block text-lg font-medium text-white mb-3">
              Execute Commands
            </label>
            <div className="relative">
              <textarea
                id="execute"
                required
                value={execute}
                onChange={(e) => setExecute(e.target.value)}
                rows={8}
                className="mt-1 block w-full rounded-lg border-2 border-gray-700 bg-gray-800 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-lg py-4 px-5 font-mono"
                placeholder="Enter each command in a new line&#10;e.g., pptransaction %player% give 40 Top up&#10;adaygdonate 40 40.000 %player%"
              />
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-gray-800 to-transparent pointer-events-none" />
            </div>
            <div className="mt-3 flex items-center space-x-2">
              <div className="w-5 h-5 rounded-full bg-primary-500/20 border border-primary-500/40 flex items-center justify-center">
                <span className="text-primary-500 text-sm">i</span>
              </div>
              <p className="text-base text-gray-400">
                Each line will be executed as a separate command. Use %player% as a placeholder for the username.
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center space-x-3 text-red-500 bg-red-500/10 border-2 border-red-500/20 rounded-lg p-5">
          <svg className="h-6 w-6 text-red-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <p className="text-lg">{error}</p>
        </div>
      )}

      <div className="flex justify-end space-x-5 pt-8 border-t-2 border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-8 py-4 text-lg font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 border-2 border-gray-700 hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-primary-500 rounded-lg transition-colors duration-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-8 py-4 text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-primary-500 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center transition-colors duration-200"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent mr-3" />
              Saving...
            </>
          ) : (
            <>{product ? 'Update Product' : 'Add Product'}</>
          )}
        </button>
      </div>
    </form>
  );
} 